import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsEmployeeHoursByJobRoleData } from "src/features/Reports/types/report.types";
import { REPORTS_EMPLOYEE_HOURS_BY_JOB_ROLE } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";

const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";

interface EmployeeHoursByJobRoleReportPageProps {
  embedded?: boolean;
  input?: ReportQueryInput | null;
}

export default function EmployeeHoursByJobRoleReportPage({
  embedded,
  input: inputProp,
}: EmployeeHoursByJobRoleReportPageProps = {}) {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const inputBuilt = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );
  const input = embedded ? inputProp ?? null : inputBuilt;
  const { data, loading, error } = useReportQuery<ReportsEmployeeHoursByJobRoleData>(
    REPORTS_EMPLOYEE_HOURS_BY_JOB_ROLE,
    "reportsEmployeeHoursByJobRole",
    input
  );

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  const rowsByRole = useMemo(() => {
    const byRole = new Map<string, { jobRoleName: string; totalHours: number; employeeCount: number }>();
    for (const row of rows) {
      const key = row.jobRoleName ?? "—";
      const existing = byRole.get(key);
      const hours = row.totalHours ?? 0;
      if (existing) {
        existing.totalHours += hours;
        existing.employeeCount += 1;
      } else {
        byRole.set(key, {
          jobRoleName: key,
          totalHours: hours,
          employeeCount: 1,
        });
      }
    }
    return Array.from(byRole.values()).sort((a, b) => a.jobRoleName.localeCompare(b.jobRoleName, undefined, { sensitivity: "base" }));
  }, [rows]);

  const content = (
    <>
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <div className="w-full bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse table-auto">
              <thead>
                <tr>
                  <th className={`pl-4 pr-2 ${headerCellClass}`}>Job Role</th>
                  <th className={`px-2 ${headerCellClass}`}>Employees</th>
                  <th className={`px-2 ${headerCellClass}`}>Total Hours</th>
                  <th className={`px-2 ${headerCellClass}`}>Avg Hours / Employee</th>
                </tr>
              </thead>
              <tbody>
                {rowsByRole.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-[#6b7280] text-sm">
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  rowsByRole.map((row, i) => (
                    <tr
                      key={i}
                      className="border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#F9FAFB]"
                    >
                      <td className="py-2 pl-4 pr-2">{row.jobRoleName}</td>
                      <td className="py-2 px-2">{row.employeeCount}</td>
                      <td className="py-2 px-2">{row.totalHours.toFixed(1)}</td>
                      <td className="py-2 px-2">
                        {row.employeeCount > 0 ? (row.totalHours / row.employeeCount).toFixed(1) : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}
        {content}
      </>
    );
  }
  return (
    <ReportPageLayout
      hideTitle
      datePreset={datePreset}
      onDatePresetChange={(preset) => setDatePreset(preset)}
      error={error}
    >
      {content}
    </ReportPageLayout>
  );
}
